import { Component, EventEmitter, forwardRef, inject, Input, Output, ViewChild } from '@angular/core';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatInput, MatInputModule } from '@angular/material/input';
import { Subject, debounceTime, filter, tap, switchMap } from 'rxjs';
import { FilterChip } from '../../models/filter-chip';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { ADGroup } from '../../models/ad-group';
import { ADADGroupService } from '../../services/adGroup.service';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-ad-group-chooser',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatChipsModule,
    MatIconModule,
    MatButtonModule,
    MatAutocompleteModule,
    MatInputModule
  ],
  templateUrl: './ad-group-chooser.component.html',
  styleUrl: './ad-group-chooser.component.scss',
    providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AdGroupChooserComponent),
      multi: true
    }
  ]
})
export class AdGroupChooserComponent {
  @Input() label: string = 'Filter';
  @Input() hint: string = '';
  @Input() chips: string[] = []; // List of possible chip types
  @Output() adGroupsChange = new EventEmitter<ADGroup[]>();
  @ViewChild(MatInput) inputElement!: MatInput;
  @ViewChild(MatAutocompleteTrigger) autocomplete!: MatAutocompleteTrigger;
  readonly debouncer: Subject<ADGroup[]> = new Subject<ADGroup[]>();
  _options = new Subject<ADGroup[]>();
  options = this._options.asObservable();
  _dialog = inject(MatDialog);

  _filter = new Subject<string>();
  filter = this._filter.asObservable();

  filterControl = new FormControl('');
  @Input() selectedADGroups: ADGroup[] = [];
  activeChip: FilterChip | null = null; // Tracks chip being edited

  onChange: (value: ADGroup[]) => void = () => { };
  onTouched: () => void = () => { };
  loading = true;

  constructor(private adGroupService: ADADGroupService) {
    this.debouncer.pipe(debounceTime(250)).subscribe(v => this.adGroupsChange.emit(this.selectedADGroups))
    this.filterControl.valueChanges.pipe(
      filter(c => c != null && typeof c === "string"),
      filter(c => !!c.trim()),
      debounceTime(250),
      tap(v => { this.loading = true; console.log(v); }),
      switchMap(c => this.adGroupService.filterADGroups([{ chip: null, value: c?.trim() }], 5, 0, "fullname", "asc"))
    ).subscribe(resp => {
      this._options.next(resp.groups);
      this.loading = false;
    });
  }

  ngOnChanges() {
    this.debouncer.next(this.selectedADGroups);// Initial emit, if there are any...
  }

  /**
   * Handles Enter key when inputting free text filters.
   */
  addFreeTextFilter(event: MatChipInputEvent): void {
    if (this.loading) return;
    console.info(this.addFreeTextFilter.name);
    // if (!rawValue) {
    //   this.autocomplete.closePanel(); // Assume the adGroup is trying to clear the autocomplete with a second "enter" press.
    //   return; // Ignore empty values
    // }
    const adGroup = this.autocomplete.activeOption?.value;
    if (adGroup && !this.selectedADGroups.find(su => su.displayName == adGroup.name)) {
      this.selectedADGroups.push(adGroup);
    }

    // Clear input field after adding a filter
    //this.filterControl.setValue('');
    event.chipInput!.clear();
    this.updateValue();
  }

  /**
   * Removes a selected filter chip.
   */
  removeADGroup(adGroup: ADGroup): void {
    this.selectedADGroups = this.selectedADGroups.filter(f => f !== adGroup);
    this.updateValue();
  }

  handleEnterKey(event: Event) {
    if (this.loading) return;
    this.filterControl.setValue('');
    this.inputElement.value = '';
    // console.debug(this.handleEnterKey.name);
    // if (this.autocomplete.activeOption) {
    //   // Manually emit optionSelected behavior
    //   this.selectADGroup({ option: { value: this.autocomplete.activeOption.value } } as MatAutocompleteSelectedEvent);
    //   this.filterControl.setValue('');
    //   event.preventDefault(); // Prevents chip input from handling Enter
    // }
  }

  /**
   * Handles chip selection from autocomplete.
   * - Moves the input into chip-edit mode.
   */
  selectADGroup(event: MatAutocompleteSelectedEvent): void {
    console.debug(this.selectADGroup.name, event);
    // console.info(this.selectADGroup.name);
    const selectedADGroup = event.option.value;
    if (!selectedADGroup || this.selectedADGroups.find(su => su.group == selectedADGroup.group))
      return;
    this.filterControl.setValue('');
    this.inputElement.value = '';
    this.selectedADGroups.push(selectedADGroup);
    this.updateValue();
  }

  private updateValue() {
    this.onChange(this.selectedADGroups);
    this.debouncer.next(this.selectedADGroups);
  }
  // **ControlValueAccessor Methods**
  writeValue(value: ADGroup[]): void {
    this.selectedADGroups = value || [];
  }

  registerOnChange(fn: (value: ADGroup[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.filterControl.disable();
    } else {
      this.filterControl.enable();
    }
  }
}
